import { useState } from "react";
import { Button, Modal, Tooltip } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import WarningImage from "../../assets/images/Warning.png";

// Reusable "Delete all notices" action for the portal notice lists
// (GST / Income Tax / TDS). It exists so the delete-everything-in-scope logic
// lives in ONE place instead of being copy-pasted into three portal pages.
//
// How it maps to the required scope: each page passes `fetchAllIds`, which is
// that page's existing "fetch all for export" call mapped down to ids. That
// call already respects the current list scope (client + active filters + the
// active tab), so:
//   - on the all-clients "total notices" page it returns every client's notices
//   - on a single client's page it returns only that client's notices
// The gathered ids are handed to the page's own `deleteByIds`, which reuses that
// portal's existing delete endpoint / doctype. No new backend endpoint is added:
// this is the same delete the "Bulk Delete" (selected) button already performs,
// fanned out over the whole current list.
//
// A simple confirmation popup is shown first, stating the exact count that will
// be removed. `deleteByIds` is expected to throw on failure so the toast + list
// refresh only run on success.
// `describe` / `okLabel` are optional overrides for callers whose ids are NOT
// notice ids. The Income-Tax Total Notices picker deletes by CLIENT id (one
// call to delete-client-documents removes every notice of those clients), so
// `ids.length` there is a client count and the default "delete N notices"
// wording would state a wrong number. Both default to the existing text, so
// every current caller renders byte-identically.
export default function DeleteAllNoticesButton({
  fetchAllIds,
  deleteByIds,
  onDone,
  noun = "notices",
  scopeHint = "",
  disabled = false,
  className = "mr-2",
  describe = null,
  okLabel = null,
}) {
  const [gathering, setGathering] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState([]);

  const handleClick = async () => {
    setGathering(true);
    try {
      const all = await fetchAllIds();
      const clean = (all || []).filter(Boolean);
      if (clean.length === 0) {
        toast.info(`No ${noun} to delete`);
        return;
      }
      setIds(clean);
      setOpen(true);
    } catch (e) {
      toast.error(e?.message || e || `Could not load ${noun}`);
    } finally {
      setGathering(false);
    }
  };

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await deleteByIds(ids);
      toast.info(`Deleted ${ids.length} ${noun}`);
      setOpen(false);
      setIds([]);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e?.message || e || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const button = (
    <Button
      danger
      type="primary"
      icon={<DeleteOutlined />}
      className={className}
      loading={gathering}
      disabled={disabled || gathering}
      onClick={handleClick}
    >
      Delete All Notices
    </Button>
  );

  return (
    <>
      {disabled ? (
        <Tooltip title={`No ${noun} to delete`}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <Modal
        title=""
        open={open}
        onOk={handleConfirm}
        onCancel={() => !deleting && setOpen(false)}
        okText={okLabel ? okLabel(ids.length) : `Delete ${ids.length} ${noun}`}
        okButtonProps={{ danger: true, loading: deleting }}
        cancelButtonProps={{ disabled: deleting }}
        centered
        zIndex={9999}
        style={{ top: -50 }}
      >
        <div className="flex items-center justify-center flex-col">
          <img src={WarningImage} width={200} height={200} alt="" />
          <p style={{ textAlign: "center", fontWeight: 600, marginBottom: 4 }}>
            {describe
              ? describe(ids.length)
              : `This will permanently delete ${ids.length} ${noun}${
                  scopeHint ? " " + scopeHint : ""
                }.`}
          </p>
          <p style={{ textAlign: "center", color: "#dc2626", margin: 0 }}>
            This action cannot be undone.
          </p>
        </div>
      </Modal>
    </>
  );
}
